import { get, set } from 'lodash';
import moment from 'moment';

// local dependencies
import { HttpService } from 'core/modules/network-service';
import { SERVICE_RESPONSE_STATUS, CALL_ACTIVITY_STATE } from 'constants/common';
import { getVisibleItems } from 'resolvers/getVisibleEntities';
import { getStore } from 'store';
import { REDUCER } from 'constants/entity-names';

// get store
const store = getStore();

/*************************************************************/

/**
 * @type {class}
 * @desc CallControlWidget defines an HTTP service interface to send HTTP network requests.
 * @example
 * import { CallControlWidgetService } from 'services/callControlWidget';
 *
 * // create service instance and send/cancel request
 * const callControlWidgetService = new CallControlWidgetService();
 * const result = await callControlWidgetService.fetchData( '<user-id>' ); // fetch data
 * callControlWidgetService.cancel(); // cancel an active request
 */
export class CallControlWidgetService extends HttpService {

    // fetch call control widget data
    async fetchComponentData( { url = {}, data = {} } = {} ) {

        // get ongoing call info from the redux store
        const callId = get( store.getState()[ REDUCER.CALL_STATE ], 'callId', null );

        try {
            const response = await this.post( {
                host: CONFIG.host,
                path: url,
                data: {
                    context: { callId, patientId: data.patientId },
                    data: data,
                }
            } );

            // extract component data
            const visibility = get( response, 'body.component.visibility', {} );
            const options = get( response, 'body.component.data.options', {} );
            const callInitiationActions = get( response, 'body.component.structure.callInitiationActions', [] );
            const callControlActions = get( response, 'body.component.structure.callControlActions', [] );
            const callPauseActions = get( response, 'body.component.structure.callPauseActions', [] );
            const callResumeActions = get( response, 'body.component.structure.callResumeActions', [] );
            const callDispositionWidgets = get( response, 'body.component.structure.callDispositionWidgets', [] );

            return {
                status: get( response, 'body.status', SERVICE_RESPONSE_STATUS.FAILURE ),
                data: {
                    options,
                    callInitiationActions: getVisibleItems( callInitiationActions, visibility ),
                    callControlActions: getVisibleItems( callControlActions, visibility ),
                    callPauseActions: getVisibleItems( callPauseActions, visibility ),
                    callResumeActions: getVisibleItems( callResumeActions, visibility ),
                    callDispositionWidgets: getVisibleItems( callDispositionWidgets, visibility ),
                },
            };
        } catch( error ) {
            return {
                status: SERVICE_RESPONSE_STATUS.FAILURE,
                data: null,
            };
        }
    }

    // initiate an inbound call
    async initiateInboundCall( { url = {}, data = {} } = {} ) {

        try {
            const response = await this.post( {
                host: CONFIG.host,
                path: url,
                data: {
                    context: {},
                    data: data,
                }
            } );

            // set the callStartTime to local UTC time
            set( response, 'body.component.data.state.callStartTime', moment.utc().format() );

            return {
                status: get( response, 'body.status', SERVICE_RESPONSE_STATUS.FAILURE ),
                data: get( response, 'body.component.data.state', {} ),
            };
        } catch( error ) {
            return {
                status: SERVICE_RESPONSE_STATUS.FAILURE,
                data: null,
            };
        }
    }

    async transferCall( { url = {}, data = {} } = {} ) {

        // get ongoing call info from the redux store
        const callId = get( store.getState()[ REDUCER.CALL_STATE ], 'callId', null );
        const callType = get( store.getState()[ REDUCER.CALL_STATE ], 'callType', null );

        try {
            const response = await this.post( {
                host: CONFIG.host,
                path: url,
                data: {
                    context: { callId, callType, patientId: data.patientId  },
                    data: data,
                }
            } );

            return {
                status: get( response, 'body.status', SERVICE_RESPONSE_STATUS.FAILURE ),
                data: get( response, 'body.component.data.state', get( response, 'body.component.data', {} ) ),
            };
        } catch( error ) {
            return {
                status: SERVICE_RESPONSE_STATUS.FAILURE,
                data: null,
            };
        }
    }

    async retrieveCall( { url = {}, data = {} } = {} ) {

        // get ongoing call info from the redux store
        const callId = get( store.getState()[ REDUCER.CALL_STATE ], 'callId', null );
        const callType = get( store.getState()[ REDUCER.CALL_STATE ], 'callType', null );


        try {
            const response = await this.post( {
                host: CONFIG.host,
                path: url,
                data: {
                    context: { callId, callType, patientId: data.patientId  },
                    data: data,
                }
            } );

            return {
                status: get( response, 'body.status', SERVICE_RESPONSE_STATUS.FAILURE ),
                data: get( response, 'body.component.data.state', get( response, 'body.component.data', {} ) ),
            };
        } catch( error ) {
            return {
                status: SERVICE_RESPONSE_STATUS.FAILURE,
                data: null,
            };
        }
    }

    // end ongoing call
    async endCall( { url = {}, data = {} } = {} ) {

        // get ongoing call info from the redux store
        const callId = get( store.getState()[ REDUCER.CALL_STATE ], 'callId', null );
        const callType = get( store.getState()[ REDUCER.CALL_STATE ], 'callType', null );

        try {
            const response = await this.post( {
                host: CONFIG.host,
                path: url,
                data: {
                    context: { callId, callType, patientId: data.patientId  },
                    data: data,
                }
            } );
    
            return {
                status: get( response, 'body.status', SERVICE_RESPONSE_STATUS.FAILURE ),
                data: {
                    callState: get( response, 'body.component.data.state.callState', CALL_ACTIVITY_STATE.ENDED ),
                    callEndTime: get( response, 'body.component.data.state.callEndTime', ( new Date() ).toUTCString() ),
                },
            };
        } catch( error ) {
            return {
                status: SERVICE_RESPONSE_STATUS.FAILURE,
                data: null,
            };
        }
    }
}
